// axios
import axios from 'axios'
import router from './router'
import { refreshHomeLocation, resetLocationStore } from './store/location'
import { isAuthRoute, sessionExpiredTarget, storedRole } from './utils/sessionExpiry'
import { clearSharedGets, isWrite } from './utils/sharedGet'
import { clearDashCache } from './components/dashboard/dashUtils'

const baseURL = process.env.VUE_APP_API_BASE_URL_USER || ''

const api = axios.create({
  baseURL
})

// Attach Authorization header if token present
api.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('accessToken')
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (e) {}
    return config
  },
  (error) => Promise.reject(error)
)

// Global response handler for unauthorized sessions.
// TB-080: the login page matches the party (doctor / establishment / patient),
// and a 401 on the auth pages themselves (wrong password, expired OTP) is left
// to the page: no clearing and no redirect, so there is no loop.
// A page fires several calls at once, so several 401s arrive together. The
// first one reads the party and clears the session; the later ones find no
// stored user, so they reuse the first target instead of guessing again.
let lastTarget = null
let lastTargetAt = 0
const SAME_EXPIRY_MS = 10000

/** Clears the session and sends the user to the login of their party. */
export function handleUnauthorized () {
  let current = null
  try { current = router?.currentRoute?.value || null } catch (e) { current = null }
  if (!current) current = { name: '', path: window.location.pathname }
  if (isAuthRoute(current)) return
  let role = null
  try { role = storedRole(localStorage) } catch (e) { role = null }
  let target = null
  let alreadyRedirecting = false
  if (!role && lastTarget && Date.now() - lastTargetAt < SAME_EXPIRY_MS) {
    target = lastTarget
    alreadyRedirecting = true
  } else {
    target = sessionExpiredTarget(current, role)
    lastTarget = target
    lastTargetAt = Date.now()
  }
  try {
    localStorage.clear()
  } catch (e) { /* storage blocked: nothing to clear */ }
  // TB-063: forget the patient's location with the session.
  resetLocationStore()
  // The first 401 of a burst navigates; a second push to the same place
  // would only race it.
  if (!target || alreadyRedirecting) return
  const href = target.query && target.query.party ? `${target.path}?party=${encodeURIComponent(target.query.party)}` : target.path
  try {
    router.push(target).catch(() => {
      window.location.href = href
    })
  } catch (e) {
    window.location.href = href
  }
}

// TB-109: after any successful write (book, cancel, complete, schedule change...)
// drop the shared reads and the dashboard cache, so no screen shows stale numbers.
export function afterWrite (response) {
  if (response && isWrite(response.config)) {
    clearSharedGets()
    clearDashCache()
    // TB-161: a saved address change moves the distance origin.
    if (/update-profile/.test(String(response.config.url || ''))) refreshHomeLocation()
  }
  return response
}
// TB-131 phase 3: a 402 OWN_PRACTICE_LOCKED / SEATS_EXCEEDED /
// CLINIC_SUBSCRIPTION_INACTIVE answer shows one prompt wherever it comes from
// (with a button to the subscription screen for the account that pays).
// error.handled tells the caller the message was already shown.
const BILLING_CODES = ['OWN_PRACTICE_LOCKED', 'SEATS_EXCEEDED', 'CLINIC_SUBSCRIPTION_INACTIVE']
export function onBillingError (error) {
  const res = error && error.response
  if (!res || res.status !== 402) return
  const code = res.data && res.data.code
  if (code && !BILLING_CODES.includes(code)) return
  error.handled = true
  error.billing = code || 'OWN_PRACTICE_LOCKED'
  import('./utils/billingApi').then((m) => m.billingPrompt(error, { router })).catch(() => {})
}

axios.interceptors.response.use(afterWrite, (error) => {
  onBillingError(error)
  return Promise.reject(error)
})

api.interceptors.response.use(
  (response) => afterWrite(response),
  (error) => {
    if (error?.response?.status === 401) handleUnauthorized()
    onBillingError(error)
    return Promise.reject(error)
  }
)

export default api
